import type {
  Blueprint,
  SentinelBlueprintDto,
  SentinelWorkspaceDetailWireDto,
} from '@/modules/sentinel-workspace/types';

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function executionPlan(responsePolicy: string): string {
  const match = responsePolicy.match(
    /(?:^|\s)EXECUTION\s*:\s*([\s\S]*?)(?=\s+(?:OUT\s+OF\s+SCOPE|SCOPE)\s*:|$)/i,
  );
  return match?.[1]?.trim() || responsePolicy;
}

export function blueprintSteps(blueprint: Record<string, unknown>): Blueprint['steps'] {
  const steps = Array.isArray(blueprint['steps']) ? blueprint['steps'] : [];
  if (steps.length > 0) {
    return steps.map((step, index) => {
      const value = step && typeof step === 'object' ? (step as Record<string, unknown>) : {};
      const stepId = typeof value['step_id'] === 'string' ? value['step_id'] : value['stepId'];
      const toolName = value['tool_name'] ?? value['toolName'];
      return {
        id: typeof stepId === 'string' ? stepId : `step-${index + 1}`,
        title: typeof value['title'] === 'string' ? value['title'] : stringValue(toolName, `Step ${index + 1}`),
        description: stringValue(value['description']),
      };
    });
  }

  const responsePolicy = stringValue(blueprint['responsePolicy']);
  const customerQuestions = Array.isArray(blueprint['customerQuestions'])
    ? blueprint['customerQuestions'].filter((question): question is string => typeof question === 'string')
    : [];

  return [
    ...(responsePolicy
      ? [{ id: 'execution-plan', title: 'Execution Plan', description: executionPlan(responsePolicy) }]
      : []),
    ...(customerQuestions.length > 0
      ? [{ id: 'customer-input', title: 'Information needed from customer', description: customerQuestions.join(' ') }]
      : []),
  ];
}

export function currentBlueprintFromApi(dto: SentinelWorkspaceDetailWireDto): Blueprint | null {
  const event = dto.aiEventLogs
    .filter((item) => item.event_kind === 'blueprint_revision')
    .sort((left, right) => (right.blueprint_revision_number ?? 0) - (left.blueprint_revision_number ?? 0))[0];
  if (!event) return null;

  const review = event.extra_details?.['blueprintReview'];
  if (!review || typeof review !== 'object' || Array.isArray(review)) return null;
  const blueprints = (review as Record<string, unknown>)['blueprints'];
  if (!Array.isArray(blueprints) || !blueprints[0] || typeof blueprints[0] !== 'object') return null;

  const blueprint = blueprints[0] as Record<string, unknown>;
  return {
    aiEventLogId: event.id,
    id: typeof blueprint['blueprintId'] === 'string' ? blueprint['blueprintId'] : event.trace_id,
    version: event.blueprint_revision_number ?? 1,
    intent: stringValue(blueprint['intent']),
    steps: blueprintSteps(blueprint),
  };
}

export function blueprintFromApi(dto: SentinelBlueprintDto): Blueprint {
  const blueprint = dto.blueprint as unknown as Record<string, unknown>;
  return {
    aiEventLogId: 0,
    id: dto.blueprintId,
    version: dto.version,
    intent: dto.blueprint.intent,
    steps: blueprintSteps(blueprint),
  };
}
